/* ORION: the purchase of the refreshed product page of the other types (snippets/orion-product-refresh.liquid,
   snippets/orion-buy-options.liquid). The pills of each option choose the variant; the form's id, the price and the
   price before the discount, the name of the choice, the availability and the button's words, the facts that follow
   the variant, the picture of the variant in the gallery and ?variant= in the address follow it. The quantity is a
   whole number from 1, with − and +; a product sold only from stock keeps it within what is left. The cart gets what
   the old pages sent: the variant's id and the quantity (Dawn's <product-form>, assets/product-form.js), and for the
   metal beds and the bed bases their options[...] and properties (measure() below).
   On a phone a bar with the price and the button stays at hand while the purchase is out of view, as on the
   mattresses (sections/orion-mattress-product.liquid). Every picture sits on the dark gallery as the mattresses' cut-outs do. */
(() => {
  function init(root) {
    if (root.dataset.orionBuyReady) return;
    root.dataset.orionBuyReady = 'true';
    let variants = [];
    try { variants = JSON.parse(root.querySelector('[data-orion-variants]')?.textContent || '[]'); } catch (_) { variants = []; }
    const groups = [...root.querySelectorAll('[data-orion-option-group]')];
    const form = document.getElementById(root.dataset.form);
    const variantInput = root.querySelector('.product-variant-id');
    const quantityInput = root.querySelector('[data-orion-quantity]');
    const addButton = root.querySelector('.orion-buy__add');
    const addLabel = addButton?.querySelector('span');
    const mobileSubmit = root.querySelector('[data-orion-mobile-submit]');
    const mobileLabel = mobileSubmit?.textContent || '';
    const priceNodes = root.querySelectorAll('[data-orion-price], [data-orion-order-price], [data-orion-mobile-price]');
    const compareNode = root.querySelector('[data-orion-compare]');
    // An offer (snippets/orion-product-refresh.liquid, kind 'offer'): the saving in whole percent, and each model's own
    // facts and code (data-orion-model, snippets/orion-offer-facts.liquid), shown only for the chosen model.
    const saveNode = root.querySelector('[data-orion-save]');
    const modelNodes = root.querySelectorAll('[data-orion-model]');
    const nameNodes = root.querySelectorAll('[data-orion-selected-size]');
    const formatter = new Intl.NumberFormat('el-GR', { style: 'currency', currency: root.dataset.currency || 'EUR' });
    const money = cents => formatter.format((Number(cents) || 0) / 100);
    // The materials (snippets/orion-product-refresh.liquid: kinds meter, foam and material).
    const material = ['material', 'meter', 'foam'].includes(root.dataset.orionProduct);
    // Sizes sold only from stock (snippets/orion-buy-stock.liquid, "variant id:left" pairs).
    const stockLine = root.querySelector('[data-orion-stock]');
    const stock = Object.fromEntries((stockLine?.dataset.orionStock || '').split(',').filter(Boolean).map(pair => {
      const [id, left] = pair.split(':');
      return [id, Number(left)];
    }));

    let current = variants.find(item => String(item.id) === variantInput?.value) || variants[0];
    let selection = current ? [...current.o] : [];
    const match = choice => variants.find(item => item.o.every((value, index) => value === choice[index]));

    // Each pill shows whether its choice, with the others as they are, can be bought. In the materials' groups
    // (data-orion-only-offered; owner, 2026-10-14: only what is available, in a grid) a value that no available variant
    // offers beside the choices of the groups before it is not shown at all; a product sold out as a whole keeps them all.
    const anyAvailable = variants.some(item => item.a);
    const offered = (index, value) => !anyAvailable || variants.some(item => item.a && item.o[index] === value && selection.slice(0, index).every((chosen, j) => item.o[j] === chosen));
    function syncPills() {
      let shown = false;
      groups.forEach(group => {
        const index = Number(group.dataset.orionOptionGroup);
        const onlyOffered = group.hasAttribute('data-orion-only-offered');
        group.querySelectorAll('button[data-value]').forEach(button => {
          const value = button.dataset.value;
          const active = selection[index] === value;
          const choice = [...selection]; choice[index] = value;
          const candidate = match(choice);
          const off = onlyOffered ? false : !candidate || !candidate.a;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
          button.classList.toggle('is-unavailable', off);
          if (onlyOffered && button.hidden !== !offered(index, value)) { button.hidden = !button.hidden; shown = true; }
          const note = button.querySelector('[data-orion-off]');
          if (note) note.hidden = !off;
        });
      });
      // A pill that came or went changes the grid: its names are measured again (a resize does the same, below).
      if (shown) hugNames();
    }

    // A colour's name that takes two lines in the materials' grid is as wide as its longer line, so that the dot and
    // the name stand centred together, as they do on one line (2026-10-14).
    const names = root.querySelectorAll('.orion-buy__options--grid .orion-buy__name');
    function hugNames() {
      names.forEach(name => { name.style.width = ''; });
      const widths = [...names].map(name => {
        if (!name.getClientRects().length) return 0;
        const range = document.createRange();
        range.selectNodeContents(name);
        const lines = [...range.getClientRects()];
        return lines.length > 1 ? Math.ceil(Math.max(...lines.map(line => line.width))) : 0;
      });
      names.forEach((name, index) => { if (widths[index]) name.style.width = `${widths[index]}px`; });
    }
    if (names.length) {
      document.fonts?.ready.then(hugNames);
      if ('ResizeObserver' in window) {
        const watch = new ResizeObserver(hugNames);
        groups.filter(group => group.classList.contains('orion-buy__options--grid')).forEach(group => watch.observe(group));
      }
    }

    // The facts read from the variant: the tile and the scene it opens. A tile names what it follows
    // (data-orion-fact-variant): nothing, its size (d); o0, o1…, the chosen pill of that option, by its name and, for a
    // colour, with its swatch; sku, the variant's SKU (materials); sel and the bed's own keys, set by the size and
    // configurator code below.
    function setFact(tile, text, swatch) {
      if (!text) return;
      tile.dataset.value = text;
      const value = tile.querySelector('strong');
      if (value) value.textContent = text;
      if (swatch !== undefined) tile.style.setProperty('--chip', swatch || 'transparent');
      const scene = root.querySelector(`template[data-orion-fact-scene="${tile.dataset.orionFact}"]`);
      const sceneValue = scene?.content.querySelector('.orion-fact-scene__value');
      if (sceneValue) sceneValue.textContent = text;
      const sceneSymbol = scene?.content.querySelector('.orion-fact-scene');
      if (sceneSymbol && swatch !== undefined) sceneSymbol.style.setProperty('--chip', swatch || 'transparent');
    }
    function syncFacts(variant) {
      root.querySelectorAll('[data-orion-fact-variant]').forEach(tile => {
        const key = tile.dataset.orionFactVariant || 'd';
        if (key === 'd') { setFact(tile, variant.d); return; }
        if (key === 'sku') { setFact(tile, variant.s); return; }
        if (key[0] !== 'o') return;
        const group = root.querySelector(`[data-orion-option-group="${key.slice(1)}"]`);
        const pill = group?.querySelector('button.is-active');
        if (pill) setFact(tile, pill.dataset.name || pill.textContent.trim(), pill.style.getPropertyValue('--swatch').trim());
      });
    }

    // The variant's own picture comes to the front of the gallery, found by its id (the gallery's data-image-id), or by
    // its place in a gallery without ids (a curated one). A variant without a picture of its own leaves the gallery
    // where it is. On the materials (owner, 2026-10-14: «διαλέγεις χρώμα… πάει στο επόμενο») a choice also holds the
    // autoplay, so the picture stays on what was chosen, and a picture not fetched yet shows at once from its small copy.
    function viewOf(gallery, variant) {
      if (!variant) return -1;
      if (!gallery._views.some(view => view.dataset.imageId)) return variant.i >= 0 ? variant.i : -1;
      return variant.m ? gallery._views.findIndex(view => view.dataset.imageId === String(variant.m)) : -1;
    }
    function showPicture(variant, attempt = 0) {
      const gallery = root.querySelector('orion-mattress-gallery');
      if (!gallery) return;
      // The gallery may not be ready yet (its script upgrades it, then it loads its first picture).
      if (!gallery._shown) { if (attempt < 40) setTimeout(() => showPicture(variant, attempt + 1), 250); return; }
      if (typeof gallery.goTo !== 'function') return;
      if (material) gallery.holdPresentation?.('orion-variant', true);
      const index = viewOf(gallery, variant);
      if (index < 0) return;
      // A colour's own picture waiting hidden (the gallery snippet's colour_views) comes in for the chosen variant; the
      // other colour's goes back once the change is over (owner, 2026-10-15: «δεν πρέπει να μπορείς να δεις τα άλλα»).
      if (gallery._views[index].hasAttribute('data-colour-view')) {
        wantedView = index;
        if (gallery._views[index].hidden) { gallery._views[index].hidden = false; gallery.syncControls?.(); }
      }
      if (index === (gallery._destination ?? gallery._index)) { parkColours(gallery); return; }
      if (material) quickPicture(gallery._views[index].querySelector('img'), variant.v);
      Promise.resolve(gallery.goTo(index, index > gallery._index ? 1 : -1)).then(() => parkColours(gallery));
    }
    let wantedView = -1;
    function parkColours(gallery) {
      // Only once the gallery has settled: no change under way, none waiting, no picture still loading for one.
      if (gallery._moving || gallery._pending || gallery._destination != null || gallery.hasAttribute('aria-busy')) {
        setTimeout(() => parkColours(gallery), 200);
        return;
      }
      let changed = false;
      gallery._views.forEach((view, at) => {
        if (!view.hasAttribute('data-colour-view')) return;
        const keep = at === wantedView || at === gallery._index;
        if (view.hidden === keep) { view.hidden = !keep; changed = true; }
      });
      if (changed) gallery.syncControls?.();
    }
    // The small copy (variant.v, fetched once the choices came near) stands in, softly, until the sharp one has arrived;
    // the gallery's change then starts at once instead of after the whole picture.
    function quickPicture(image, small) {
      if (!image || !small || image._ready || image.getAttribute('src')) return;
      const sharp = sharpCopy(image);
      image.dataset.orionPreview = '';
      image.src = small;
      const settle = () => {
        const done = () => image.removeAttribute('data-orion-preview');
        image.addEventListener('load', done, { once: true });
        image.addEventListener('error', done, { once: true });
        image.srcset = image.dataset.srcset || '';
        image.src = image.dataset.src;
        if (image.complete) done();
      };
      sharp.decode().then(settle, settle);
    }
    // The copy the gallery would ask for (same srcset and sizes), fetched aside so that the gallery finds it ready.
    function sharpCopy(image) {
      const sharp = new Image();
      sharp.decoding = 'async';
      sharp.sizes = image.sizes;
      sharp.srcset = image.dataset.srcset || '';
      sharp.src = image.dataset.src;
      return sharp;
    }
    function warmPicture(variant) {
      const gallery = root.querySelector('orion-mattress-gallery');
      const index = gallery?._shown ? viewOf(gallery, variant) : -1;
      const image = index >= 0 ? gallery._views[index].querySelector('img') : null;
      if (!image || image._ready || image._warmed || image.getAttribute('src')) return;
      image._warmed = true;
      sharpCopy(image);
    }

    function render(variant, chosen) {
      if (!variant) return;
      current = variant;
      // The picture first: its change starts while the rest of the page follows.
      if (chosen) showPicture(variant);
      if (variantInput) variantInput.value = variant.id;
      priceNodes.forEach(node => { node.textContent = money(variant.p); });
      if (compareNode) {
        compareNode.hidden = !(variant.c > variant.p);
        compareNode.textContent = variant.c > variant.p ? money(variant.c) : '';
      }
      if (saveNode) {
        const saving = variant.c > variant.p ? Math.round((variant.c - variant.p) * 100 / variant.c) : 0;
        saveNode.hidden = saving <= 0;
        saveNode.textContent = `−${saving}%`;
      }
      modelNodes.forEach(node => { node.hidden = node.dataset.orionModel !== variant.o[Number(node.dataset.orionModelOption) || 0]; });
      if (groups.length) nameNodes.forEach(node => { node.textContent = variant.t; });
      const left = stock[variant.id];
      const soldOut = left !== undefined && left <= 0;
      const available = !!variant.a && !soldOut;
      const words = available ? root.dataset.addLabel : soldOut ? stockLine.dataset.out : root.dataset.unavailableLabel;
      if (addButton) addButton.disabled = !available;
      if (addLabel) addLabel.textContent = words;
      if (mobileSubmit) {
        mobileSubmit.disabled = !available;
        mobileSubmit.textContent = available ? mobileLabel : words;
      }
      if (stockLine) {
        stockLine.hidden = left === undefined;
        if (left !== undefined) {
          stockLine.dataset.state = left <= 0 ? 'out' : left <= 10 ? 'few' : 'in';
          stockLine.querySelector('span').textContent = left <= 0 ? stockLine.dataset.out : left === 1 ? stockLine.dataset.one
            : left <= 10 ? stockLine.dataset.few.replace('__N__', left) : stockLine.dataset.in;
        }
      }
      if (quantityInput) {
        if (left === undefined) quantityInput.removeAttribute('max');
        else {
          quantityInput.max = Math.max(1, left);
          if (Number(quantityInput.value) > Number(quantityInput.max)) quantityInput.value = quantityInput.max;
        }
      }
      // The options as the old metal bed and bed base pages sent them beside the id (options[Color] and so on).
      root.querySelectorAll('[data-orion-option-input]').forEach(input => { input.value = variant.o[Number(input.dataset.orionOptionInput)] ?? input.value; });
      syncPills();
      syncFacts(variant);
      if (own) own.update(variant);
      syncTotal();
      if (!chosen) return;
      // The address names the variant, as Shopify's own pages do, without a new step in the history.
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState(window.history.state, '', url.toString());
    }

    // The variant a pill leads to. A choice with no variant beside the others takes the nearest one that has it (an
    // available one first). Where only what is offered shows, it lands on an available variant, never on a hidden one,
    // and changes as little as it can (Boss, 2026-10-14: «Μαλακό» then «3 εκ.» went back to «Σκληρό»): of the available
    // variants with the new value it takes the one that keeps most of the other choices, the visitor's latest choices
    // first and then the groups in their order.
    const chosenOrder = [];
    const better = (a, b) => { const i = a.findIndex((value, j) => value !== b[j]); return i >= 0 && a[i] > b[i]; };
    function pick(group, button) {
      const index = Number(group.dataset.orionOptionGroup);
      const choice = [...selection]; choice[index] = button.dataset.value;
      const exact = match(choice);
      if (!group.hasAttribute('data-orion-only-offered')) {
        return exact || variants.find(item => item.o[index] === choice[index] && item.a) || variants.find(item => item.o[index] === choice[index]);
      }
      if (exact && exact.a) return exact;
      const keep = [...chosenOrder, ...selection.map((_, j) => j)].filter((j, at, all) => j !== index && all.indexOf(j) === at);
      let best = null, bestScore = null;
      variants.forEach(item => {
        if (!item.a || item.o[index] !== choice[index]) return;
        const score = keep.map(j => (item.o[j] === selection[j] ? 1 : 0));
        if (!best || better(score, bestScore)) { best = item; bestScore = score; }
      });
      return best || exact || variants.find(item => item.o[index] === choice[index]);
    }
    let intent = null;
    groups.forEach(group => {
      group.addEventListener('click', event => {
        const button = event.target.closest('button[data-value]');
        if (!button || !group.contains(button)) return;
        const variant = pick(group, button);
        if (!variant) return;
        // The group just chosen goes to the front of the visitor's choices.
        const index = Number(group.dataset.orionOptionGroup);
        const at = chosenOrder.indexOf(index);
        if (at >= 0) chosenOrder.splice(at, 1);
        chosenOrder.unshift(index);
        selection = [...variant.o];
        render(variant, true);
      });
      if (!material) return;
      // On the materials a pill under the pointer for a moment, or pressed, starts fetching its picture.
      group.addEventListener('pointerover', event => {
        const button = event.target.closest('button[data-value]');
        clearTimeout(intent);
        if (button) intent = setTimeout(() => warmPicture(pick(group, button)), 120);
      });
      group.addEventListener('pointerleave', () => clearTimeout(intent));
      group.addEventListener('pointerdown', event => {
        const button = event.target.closest('button[data-value]');
        if (button) warmPicture(pick(group, button));
      });
    });
    // A bed base's size of one's own (snippets/orion-buy-measure.liquid) or an upholstered bed's configurator
    // (snippets/orion-buy-bed.liquid), priced as their old pages priced them.
    const own = [measure, bed, metres, foam].reduce((found, mode) => found || mode(root, {
      current: () => current, priceNodes, nameNodes, addButton, addLabel, mobileSubmit, mobileLabel, money,
      setFact,
    }), null);
    // The materials' total follows the quantity (owner, 2026-10-14: «πατούσα + … και η τιμή δεν άλλαζε»): the total and
    // the phone bar show the price times the pieces, and the line under the total says so ("3 × 0,70 €, με ΦΠΑ"). A foam
    // counts its price for the size given.
    const totalNote = root.querySelector('.orion-buy__total small[data-times]');
    function syncTotal() {
      if (!material || !quantityInput || !current) return;
      const foamValue = root.querySelector('[data-orion-foam-calculated]');
      const unit = foamValue ? Math.round(Number(foamValue.value) * 100) : current.p;
      if (!(unit > 0)) return;
      const count = Math.max(1, Math.floor(Number(quantityInput.value) || 1));
      root.querySelectorAll('[data-orion-order-price], [data-orion-mobile-price]').forEach(node => { node.textContent = money(unit * count); });
      if (totalNote) totalNote.textContent = count > 1 ? totalNote.dataset.times.replace('__N__', count).replace('__P__', money(unit)) : totalNote.dataset.each;
    }
    root.querySelectorAll('[data-orion-foam] input').forEach(input => input.addEventListener('input', syncTotal));

    // Where only what is offered shows, the page never opens on a variant that cannot be bought (an old ?variant=):
    // it opens on the first available one.
    if (current && !current.a && groups.some(group => group.hasAttribute('data-orion-only-offered'))) {
      const first = variants.find(item => item.a);
      if (first) { current = first; selection = [...first.o]; }
    }
    render(current, false);
    // The materials open on the cover; a page opened on a variant (?variant=) starts on that variant's picture (the
    // gallery's data-start) and keeps it. So do the duvets (one picture at a time, the size the page opens on already
    // its gallery's start, owner 03/11). Other products bring the variant's picture forward as before.
    if (material || root.dataset.orionProduct === 'duvet') { if (current && new URL(window.location.href).searchParams.has('variant')) showPicture(current); }
    else if (current && current.i > 0) showPicture(current);

    // The materials' small copies of the variants' pictures, fetched before a choice so that it changes the picture at
    // once even on a slow connection: with a mouse when the pointer first comes to the choices, on a touch screen once
    // they come near and the page has loaded.
    const smalls = material ? [...new Set(variants.map(item => item.v).filter(Boolean))] : [];
    if (smalls.length > 1 && groups.length && !navigator.connection?.saveData) {
      let fetched = false;
      const fetchSmalls = () => {
        if (fetched) return;
        fetched = true;
        smalls.forEach(url => { const image = new Image(); image.decoding = 'async'; image.fetchPriority = 'low'; image.src = url; });
      };
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        groups.forEach(group => ['pointerenter', 'focusin'].forEach(type => group.addEventListener(type, fetchSmalls, { once: true })));
      } else if ('IntersectionObserver' in window) {
        const near = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          near.disconnect();
          const go = () => ('requestIdleCallback' in window ? requestIdleCallback(fetchSmalls, { timeout: 1500 }) : setTimeout(fetchSmalls, 200));
          if (document.readyState === 'complete') go(); else window.addEventListener('load', go, { once: true });
        }, { rootMargin: '400px 0px' });
        groups.forEach(group => near.observe(group));
      }
    }

    // Quantity: whole pieces from 1, within what is left of a size sold from stock.
    const cap = () => Number(quantityInput?.max) || Infinity;
    const settle = value => Math.min(cap(), Math.max(1, Math.floor(Number(value) || 1)));
    root.querySelector('[data-orion-quantity-minus]')?.addEventListener('click', () => { quantityInput.value = settle(Number(quantityInput.value || 1) - 1); syncTotal(); });
    root.querySelector('[data-orion-quantity-plus]')?.addEventListener('click', () => { quantityInput.value = settle(Number(quantityInput.value || 1) + 1); syncTotal(); });
    quantityInput?.addEventListener('keydown', event => { if (['.', ',', 'e', 'E', '+', '-'].includes(event.key)) event.preventDefault(); });
    quantityInput?.addEventListener('input', syncTotal);
    quantityInput?.addEventListener('change', () => { quantityInput.value = settle(quantityInput.value); syncTotal(); });
    form?.addEventListener('submit', () => { if (quantityInput) quantityInput.value = settle(quantityInput.value); }, { capture: true });

    // The phone bar: at hand whenever the purchase buttons are out of view, once the visitor has started down the
    // page, and away over the footer.
    const mobileBar = root.querySelector('[data-orion-mobile-bar]');
    const purchase = root.querySelector('.orion-mattress-product__actions');
    if (mobileBar && purchase && 'IntersectionObserver' in window) {
      mobileBar.hidden = false;
      const footer = document.querySelector('.shopify-section-group-footer-group, footer');
      let purchaseAway = false;
      let footerVisible = false;
      const update = () => mobileBar.classList.toggle('is-visible', purchaseAway && !footerVisible);
      let ticking = false;
      const check = () => {
        ticking = false;
        const box = purchase.getBoundingClientRect();
        purchaseAway = (box.bottom < 0 || box.top > window.innerHeight) && window.scrollY > 160;
        update();
      };
      window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(check); } }, { passive: true });
      check();
      if (footer) {
        new IntersectionObserver(([entry]) => { footerVisible = entry.isIntersecting; update(); }, { threshold: .01 }).observe(footer);
      }
    }
    mobileSubmit?.addEventListener('click', () => {
      if (form?.requestSubmit && addButton && !addButton.disabled) form.requestSubmit(addButton);
    });
  }

  // A bed base's size and price (snippets/orion-buy-measure.liquid): the same reading, prices and fields as the old
  // bed base page's script (snippets/orion-bed-base-script.liquid before 2026-10-02, in git); only the look is new.
  // 'size' and 'width' price by the first width step of custom.customprice at or above the width (the widest when the
  // width is past them all, the width then set to it), 'size' adds 10% over 200 cm and 20% over 210 cm of length
  // (at most 220), and both take off the variant's discount; 'variant-length' and 'variant' cost the variant's price,
  // 'fixed' the product's. The price shown goes to properties[_calculatedPrice] with two decimals.
  // An offer of several mattresses (Black Friday 2026, data-model-option): 'size' takes the chosen model's own steps
  // (custom.offer_models, each model's set) and its variant's price and discount, names the model before the size, and
  // keeps to the fields' own limits (the mattresses' 50–180 × 110–220).
  function measure(root, page) {
    const block = root.querySelector('[data-orion-measure]');
    if (!block) return null;
    const mode = block.dataset.orionMeasure;
    const unit = block.dataset.unit || 'cm';
    const base = Number(block.dataset.base) || 0;
    const compare = Number(block.dataset.compare) || 0;
    let source = null;
    try { source = JSON.parse(block.querySelector('[data-orion-measure-sets]')?.textContent || 'null'); } catch (_) { source = null; }
    const sets = source && Array.isArray(source.set) ? source.set : [];
    const rate = compare > base ? (compare - base) / compare : 0;
    let models = null;
    try { models = JSON.parse(block.querySelector('[data-orion-measure-models]')?.textContent || 'null'); } catch (_) { models = null; }
    const modelOption = Number(block.dataset.modelOption) || 0;
    const modelOf = variant => (models && variant ? variant.o[modelOption] : '');
    const setsOf = variant => { const own = models && models[modelOf(variant)]; return own && Array.isArray(own.set) ? own.set : sets; };
    const rateOf = variant => (models && variant ? (variant.c > variant.p ? (variant.c - variant.p) / variant.c : 0) : rate);
    const baseOf = variant => (models && variant ? Number(variant.p) / 100 : base);
    const widthInput = block.querySelector('[data-orion-width]');
    const lengthInput = block.querySelector('[data-orion-length]');
    const calculated = root.querySelector('[data-orion-calculated-price]');
    const hint = block.querySelector('[data-orion-measure-hint]');
    const presets = [...block.querySelectorAll('[data-orion-preset]')];
    const valid = input => input && Number.isFinite(Number(input.value)) && Number(input.value) >= Math.max(1, Number(input.min) || 1);

    function setPrice(value) {
      const rounded = Math.round((Number(value) + Number.EPSILON) * 100) / 100;
      page.priceNodes.forEach(node => { node.textContent = page.money(rounded * 100); });
      if (calculated) calculated.value = rounded.toFixed(2);
    }
    function setSelection(text) {
      page.nameNodes.forEach(node => { node.textContent = text; });
      root.querySelectorAll('[data-orion-fact-variant="sel"]').forEach(tile => page.setFact(tile, text));
    }
    function setAvailability(available, message) {
      const words = available ? root.dataset.addLabel : root.dataset.unavailableLabel;
      if (page.addButton) page.addButton.disabled = !available;
      if (page.addLabel) page.addLabel.textContent = words;
      if (page.mobileSubmit) {
        page.mobileSubmit.disabled = !available;
        page.mobileSubmit.textContent = available ? page.mobileLabel : words;
      }
      if (hint) { hint.hidden = !message; hint.textContent = message || ''; }
    }
    function stepPrice(width, variant) {
      const steps = setsOf(variant);
      if (!steps.length) return baseOf(variant);
      let selected = null;
      let maximum = steps[0];
      steps.forEach(set => {
        if (Number(set.w) > Number(maximum.w)) maximum = set;
        if (!selected && width <= Number(set.w)) selected = set;
      });
      if (!selected) {
        selected = maximum;
        if (widthInput) widthInput.value = Number(maximum.w);
      }
      return Number(selected.p) || baseOf(variant);
    }

    function update(variant) {
      variant = variant || page.current();
      if (!variant) return;
      if (mode === 'size' || mode === 'width') {
        const needsLength = mode === 'size';
        if (!valid(widthInput) || (needsLength && !valid(lengthInput))) {
          setAvailability(false, needsLength ? block.dataset.badBoth : block.dataset.badWidth);
          return;
        }
        let length = lengthInput ? Number(lengthInput.value) : null;
        if (lengthInput && length > 220) { length = 220; lengthInput.value = 220; }
        let price = stepPrice(Number(widthInput.value), variant);
        const width = Number(widthInput.value);
        const model = modelOf(variant) ? `${modelOf(variant)} · ` : '';
        if (needsLength) {
          if (length > 210) price *= 1.2;
          else if (length > 200) price *= 1.1;
          setSelection(`${model}${width} × ${length} ${unit}`);
        } else {
          setSelection(`${model}${width} ${unit}`);
        }
        const off = rateOf(variant);
        setPrice(off ? price * (1 - off) : price);
        // An offer's price before the discount is that of the size chosen, not of the variant's first step.
        const before = models && root.querySelector('[data-orion-compare]');
        if (before && off) before.textContent = page.money(Math.round(price * 100));
        setAvailability(!!variant.a);
        presets.forEach(button => {
          const active = Number(button.dataset.width) === width && (!needsLength || Number(button.dataset.length) === length);
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
        return;
      }
      if (mode === 'variant-length') {
        let length = lengthInput ? Number(lengthInput.value) : null;
        if (lengthInput && length > 220) { length = 220; lengthInput.value = 220; }
        const ok = valid(lengthInput);
        // A size that already ends in its length («101-120 × 190 εκ.») takes the length of the field in that place
        // (owner 14/10: not «… × 190 εκ. · 190 εκ.»); any other variant keeps « · length». Only the words shown change.
        const sized = ok && /^(.*\S)\s*[×x]\s*\d+(?:[.,]\d+)?\s*(?:εκ\.|cm)$/i.exec(variant.t);
        setSelection(!ok ? variant.t : sized ? `${sized[1]} × ${length} ${unit}` : `${variant.t} · ${length} ${unit}`);
        setPrice(Number(variant.p) / 100);
        setAvailability(!!variant.a && ok, ok ? '' : block.dataset.badLength);
        return;
      }
      setPrice(mode === 'fixed' ? base : Number(variant.p) / 100);
    }

    presets.forEach(button => button.addEventListener('click', () => {
      if (widthInput) widthInput.value = button.dataset.width;
      if (lengthInput && button.dataset.length) lengthInput.value = button.dataset.length;
      update();
    }));
    [widthInput, lengthInput].forEach(input => input?.addEventListener('input', () => update()));
    if (models) {
      // The mattresses' own rules for a size of one's own (sections/orion-mattress-product.liquid, the owner's): whole
      // centimetres only, within the fields' limits (50–180 × 110–220); anything but a digit is refused as it is typed or
      // pasted («120,5» pasted gives 120), and once a field is left (or the form is sent) it comes back to the nearest limit.
      const settle = input => {
        if (!input) return;
        const min = Number(input.min) || 1, max = Number(input.max) || Infinity;
        const value = Number.parseInt(input.value, 10);
        const next = String(Math.min(max, Math.max(min, Number.isFinite(value) ? value : min)));
        if (input.value !== next) input.value = next;
      };
      [widthInput, lengthInput].forEach(input => {
        if (!input) return;
        input.addEventListener('keydown', event => { if (['.', ',', 'e', 'E', '+', '-'].includes(event.key)) event.preventDefault(); });
        input.addEventListener('beforeinput', event => {
          const text = event.data ?? event.dataTransfer?.getData('text') ?? '';
          if (!/\D/.test(text)) return;
          event.preventDefault();
          const whole = event.inputType === 'insertFromPaste' ? text.trim().match(/^\d+/) : null;
          if (whole) { input.value = whole[0]; input.dispatchEvent(new Event('input', { bubbles: true })); }
        });
        input.addEventListener('change', () => { settle(input); update(); });
      });
      widthInput?.form?.addEventListener('submit', () => { settle(widthInput); settle(lengthInput); update(); }, { capture: true });
      // On a phone the fields open and close under the sizes, as on the mattresses.
      block.querySelector('[data-orion-custom-toggle]')?.addEventListener('click', event => {
        const open = event.currentTarget.getAttribute('aria-expanded') !== 'true';
        event.currentTarget.setAttribute('aria-expanded', String(open));
        event.currentTarget.closest('.orion-buy__configure')?.classList.toggle('is-custom', open);
      });
    }
    return { update };
  }

  // An upholstered bed's configurator (snippets/orion-buy-bed.liquid): the same fields, prices and properties as the
  // old bed page's script (sections/orion-bed-product.liquid before 2026-10-02, in git); only the look is new. The
  // price is the bed's step for the size, plus the base's and the mechanism's (each a number or price steps: the first
  // step whose width and length cover the size, else the last), less the bed's discount; the base cards show what
  // each adds at the size chosen.
  function bed(root, page) {
    const block = root.querySelector('[data-orion-bed]');
    if (!block) return null;
    const d = block.dataset;
    const unit = d.unit || 'cm';
    let priceSets = [];
    try { priceSets = (JSON.parse(root.querySelector('[data-orion-bed-sets]')?.textContent || 'null') || {}).set || []; } catch (_) { priceSets = []; }
    const baseVariantPrice = Number(d.base) || 0;
    const compareAtPrice = Number(d.compare) || 0;
    const discountRate = compareAtPrice > baseVariantPrice ? (compareAtPrice - baseVariantPrice) / compareAtPrice : 0;
    const variantAvailable = d.available === 'true';
    const widthInput = block.querySelector('[data-orion-bed-width]');
    const lengthInput = block.querySelector('[data-orion-bed-length]');
    const calculatedInput = root.querySelector('[data-orion-bed-calculated-price]');
    const materialStyle = block.querySelector('[data-orion-bed-style]');
    const colorCode = block.querySelector('[data-orion-bed-color-code]');
    const upholsteryFields = block.querySelector('[data-orion-bed-upholstery-fields]');
    const materialLink = block.querySelector('[data-orion-bed-material-link]');
    const mechanismPanel = block.querySelector('[data-orion-bed-mechanism-panel]');
    const validation = block.querySelector('[data-orion-bed-validation]');
    const parsePrice = value => {
      if (!value) return 0;
      try { return JSON.parse(value); } catch (_) { return Number(value) || 0; }
    };
    const matrixPrice = (source, width, length) => {
      if (typeof source === 'number') return source;
      const sets = source && Array.isArray(source.set) ? source.set : [];
      let fallback = 0;
      for (const set of sets) {
        fallback = Number(set.p) || fallback;
        if (width <= Number(set.w) && length <= Number(set.l)) return Number(set.p) || 0;
      }
      return fallback;
    };
    const selected = selector => block.querySelector(`${selector}:checked`);
    const hasStorage = () => selected('[data-orion-bed-storage]')?.dataset.orionBedStorage === 'yes';
    const setFacts = (key, text) => root.querySelectorAll(`[data-orion-fact-variant="${key}"]`).forEach(tile => page.setFact(tile, text));

    // The collection and the shade come from the library below; the fields only carry them to the cart.
    function updateMaterial() {
      const material = selected('[data-orion-bed-material]');
      const isFabric = material?.dataset.orionBedMaterial === 'fabric';
      materialStyle.disabled = !material;
      colorCode.disabled = !material;
      if (material) {
        materialLink.href = isFabric ? d.fabricUrl : d.leatherUrl;
        materialLink.querySelector('span').textContent = isFabric ? d.fabricCharts : d.leatherCharts;
      }
    }
    function updateStorage() {
      mechanismPanel.hidden = !hasStorage();
      if (!hasStorage()) {
        const none = block.querySelector('[data-orion-bed-mechanism][value="No Option"]');
        if (none) none.checked = true;
      }
    }
    function validate() {
      const width = Number(widthInput.value);
      const length = Number(lengthInput.value);
      const material = selected('[data-orion-bed-material]');
      const missing = [];
      if (!Number.isFinite(width) || width < 50 || width > 180 || !Number.isFinite(length) || length < 50 || length > 200) missing.push(d.mSize);
      if (!material) missing.push(d.mType);
      if (!materialStyle.value) missing.push(d.mStyle);
      if (!colorCode.value.trim()) missing.push(d.mCode);
      const valid = variantAvailable && missing.length === 0;
      const words = variantAvailable ? root.dataset.addLabel : root.dataset.unavailableLabel;
      if (page.addButton) page.addButton.disabled = !valid;
      if (page.addLabel) page.addLabel.textContent = words;
      if (page.mobileSubmit) { page.mobileSubmit.disabled = !valid; page.mobileSubmit.textContent = page.mobileLabel; }
      validation.textContent = valid ? d.ready : `${d.missing} ${missing.join(', ')}.`;
      validation.classList.toggle('is-ready', valid);
    }
    function update() {
      let width = Number.parseInt(widthInput.value, 10);
      let length = Number.parseInt(lengthInput.value, 10);
      if (!Number.isFinite(width) || !Number.isFinite(length)) { validate(); return; }
      if (width > 180) { width = 180; widthInput.value = 180; }
      if (length > 200) { length = 200; lengthInput.value = 200; }
      let total = priceSets.length ? matrixPrice({ set: priceSets }, width, length) : baseVariantPrice;
      const base = selected('[data-orion-bed-base]');
      const mechanism = selected('[data-orion-bed-mechanism]');
      total += matrixPrice(parsePrice(base?.dataset.price), width, length);
      total += matrixPrice(parsePrice(mechanism?.dataset.price), width, length);
      if (discountRate) total *= 1 - discountRate;
      total = Math.round((total + Number.EPSILON) * 100) / 100;
      page.priceNodes.forEach(node => { node.textContent = page.money(total * 100); });
      calculatedInput.value = total.toFixed(2);
      // What each base, and the mechanism, adds at this size.
      block.querySelectorAll('[data-orion-bed-base], [data-orion-bed-mechanism]').forEach(input => {
        const note = input.closest('label')?.querySelector('[data-orion-bed-option-price]');
        if (note) note.textContent = '+ ' + page.money(matrixPrice(parsePrice(input.dataset.price), width, length) * 100);
      });
      const size = `${widthInput.value} × ${lengthInput.value} ${unit}`;
      page.nameNodes.forEach(node => { node.textContent = size; });
      setFacts('sel', size);
      setFacts('storage', hasStorage() ? d.storageYes : d.storageNo);
      const material = selected('[data-orion-bed-material]');
      if (material) setFacts('upholstery', [material.dataset.orionBedMaterial === 'fabric' ? d.fabric : d.leather, materialStyle.value].filter(Boolean).join(' · '));
      block.querySelectorAll('[data-orion-bed-size]').forEach(button => {
        const active = Number(button.dataset.width) === Number(widthInput.value) && Number(button.dataset.length) === Number(lengthInput.value);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      validate();
    }

    // The choice of the upholstery by pictures (owner, 2026-10-15: «ναι σε όλα»; snippets/orion-buy-bed.liquid):
    // the collections the shop still shows, each by its texture, then the chosen one's available shades by their own
    // pictures (from its product's .js), named by the colour in words where the picture's description gives it (Greek
    // pages) and by the code; the chosen shade large, opening in the gallery's viewer. It fills the same Style and
    // Color-Code fields (the code as NAME-N, the old page's form), which it hides.
    const library = (() => {
      let data = null;
      try { data = JSON.parse(root.querySelector('[data-orion-bed-library]')?.textContent || 'null'); } catch (_) { data = null; }
      if (!data) return null;
      const esc = text => String(text ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      const sized = (src, width) => src ? `${src}${src.includes('?') ? '&' : '?'}width=${width}` : '';
      const codeOf = title => String(title).trim().replace(/\s*-\s*/g, '-').replace(/\s+(\d+[A-Z]?)$/, '-$1');
      const wordOf = alt => { const found = d.words === 'true' && /\sσε\s(.+?)\sαπόχρωση/.exec(alt || ''); return found ? found[1] : ''; };
      const shades = new Map();
      const box = document.createElement('div');
      box.className = 'orion-buy__library';
      box.innerHTML = `<div class="orion-buy__heading orion-buy__heading--step"><span>${esc(d.tStyle)}</span></div>
        <div class="orion-buy__sizes orion-buy__options orion-buy__options--swatches orion-buy__options--library" role="group" aria-label="${esc(d.tStyle)}" data-orion-bed-collections></div>
        <div data-orion-bed-shades-wrap hidden>
          <div class="orion-buy__heading orion-buy__heading--step"><span>${esc(d.tShade)}</span></div>
          <div class="orion-buy__sizes orion-buy__options orion-buy__options--swatches orion-buy__options--shades" role="group" aria-label="${esc(d.tShade)}" data-orion-bed-shades></div>
          <div class="orion-buy__shade" data-orion-bed-shade hidden>
            <button type="button" class="orion-buy__shade-zoom" data-orion-bed-zoom aria-label="${esc(d.tZoom)}"><img alt="" data-orion-bed-shade-picture><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 4h6v6M10 20H4v-6M20 4l-7 7M4 20l7-7"/></svg></button>
            <span><b data-orion-bed-shade-name></b><small data-orion-bed-shade-code></small></span>
          </div>
        </div>`;
      upholsteryFields.after(box);
      const collections = box.querySelector('[data-orion-bed-collections]');
      const shadesWrap = box.querySelector('[data-orion-bed-shades-wrap]');
      const shadeRow = box.querySelector('[data-orion-bed-shades]');
      const chosen = box.querySelector('[data-orion-bed-shade]');
      const picture = box.querySelector('[data-orion-bed-shade-picture]');
      const kind = () => selected('[data-orion-bed-material]')?.dataset.orionBedMaterial;
      const entries = () => data[kind()] || [];
      const press = (row, button) => row.querySelectorAll('button').forEach(other => { const on = other === button; other.classList.toggle('is-active', on); other.setAttribute('aria-pressed', String(on)); });
      function chooseShade(button, shade) {
        press(shadeRow, button);
        colorCode.value = shade.code;
        picture.src = sized(shade.src, 480);
        picture.dataset.full = sized(shade.src, 1600);
        chosen.querySelector('[data-orion-bed-shade-name]').textContent = shade.word || shade.code;
        chosen.querySelector('[data-orion-bed-shade-code]').textContent = shade.word ? shade.code : '';
        chosen.hidden = false;
        update();
      }
      async function chooseCollection(button, entry) {
        press(collections, button);
        materialStyle.value = entry.s;
        colorCode.value = '';
        chosen.hidden = true;
        shadeRow.replaceChildren();
        shadesWrap.hidden = false;
        update();
        if (!shades.has(entry.h)) {
          const rootUrl = window.Shopify?.routes?.root || '/';
          shades.set(entry.h, fetch(`${rootUrl}products/${entry.h}.js`).then(response => response.json()).then(product => (product.variants || [])
            .filter(variant => variant.available)
            .map(variant => ({ code: codeOf(variant.title), word: wordOf(variant.featured_image?.alt), src: variant.featured_image?.src || '' }))).catch(() => []));
        }
        const list = await shades.get(entry.h);
        if (materialStyle.value !== entry.s) return;
        list.forEach(shade => {
          const pill = document.createElement('button');
          pill.type = 'button';
          pill.setAttribute('aria-pressed', 'false');
          pill.title = shade.word ? `${shade.word} · ${shade.code}` : shade.code;
          pill.innerHTML = `${shade.src ? `<img class="orion-buy__swatch-picture" src="${esc(sized(shade.src, 160))}" alt="" loading="lazy" width="80" height="80">` : ''}<span>${esc(shade.code.split('-').pop())}</span>`;
          pill.addEventListener('click', () => chooseShade(pill, shade));
          shadeRow.appendChild(pill);
        });
      }
      function render() {
        upholsteryFields.hidden = true;
        collections.replaceChildren();
        shadesWrap.hidden = true;
        chosen.hidden = true;
        if (!kind()) { box.hidden = true; return; }
        box.hidden = false;
        // Only the collections shown here can be sent.
        materialStyle.innerHTML = `<option value="">${esc(d.styleChoose)}</option>` + entries().map(entry => `<option value="${esc(entry.s)}">${esc(entry.s)}</option>`).join('');
        materialStyle.value = '';
        colorCode.value = '';
        entries().forEach(entry => {
          const card = document.createElement('button');
          card.type = 'button';
          card.setAttribute('aria-pressed', 'false');
          card.innerHTML = `<img class="orion-buy__swatch-picture" src="${esc(entry.i)}" alt="" loading="lazy" width="120" height="120"><span>${esc(entry.s)}</span>`;
          card.addEventListener('click', () => chooseCollection(card, entry));
          collections.appendChild(card);
        });
      }
      box.querySelector('[data-orion-bed-zoom]').addEventListener('click', event => {
        const viewer = document.querySelector('orion-mattress-gallery')?._viewer;
        if (viewer?.openView) viewer.openView({ figure: true, image: picture, fullSrc: picture.dataset.full, returnFocus: event.currentTarget });
        else window.open(picture.dataset.full, '_blank', 'noopener');
      });
      render();
      return { render };
    })();

    block.querySelectorAll('[data-orion-bed-size]').forEach(button => button.addEventListener('click', () => { widthInput.value = button.dataset.width; lengthInput.value = button.dataset.length; update(); }));
    [widthInput, lengthInput, materialStyle, colorCode].forEach(input => input?.addEventListener('input', update));
    materialStyle?.addEventListener('change', update);
    block.querySelectorAll('[data-orion-bed-storage]').forEach(input => input.addEventListener('change', () => { updateStorage(); update(); }));
    block.querySelectorAll('[data-orion-bed-material]').forEach(input => input.addEventListener('change', () => { updateMaterial(); library?.render(); update(); }));
    block.querySelectorAll('[data-orion-bed-base],[data-orion-bed-mechanism]').forEach(input => input.addEventListener('change', update));
    updateMaterial(); updateStorage();
    return { update };
  }

  // Fabrics and faux leathers by the metre (snippets/orion-product-refresh.liquid, kind 'meter'): the same field and
  // reading as the old page (snippets/orion-meter-product.liquid before 2026-10-02, in git). The metres take digits
  // and one comma or point; on leaving the field they round to tenths (halves up), at least 1, written with a comma;
  // the cart gets quantity 1, the metres with a point in properties[Ποσότητα σε μέτρα] and the product's price
  // (product.price) times the metres, two decimals, in properties[_calculatedPrice].
  function metres(root, page) {
    const input = root.querySelector('[data-orion-metres]');
    if (!input) return null;
    const calculated = root.querySelector('[data-orion-metre-calculated]');
    const basePrice = Number(input.dataset.base) || 0;
    const form = input.form;
    const hint = root.querySelector('[data-orion-metre-hint]');
    const unit = input.dataset.unit || '';
    let available = true;
    let last = input.value;
    const read = () => {
      const text = input.value.trim().replace(',', '.');
      return /^(?:\d+(?:\.\d*)?|\.\d+)$/.test(text) ? Number(text) : NaN;
    };
    function update(variant) {
      if (variant) available = !!variant.a;
      const quantity = read();
      input.setCustomValidity(Number.isFinite(quantity) && quantity >= 1 ? '' : (hint?.dataset.min || ''));
      root.querySelectorAll('[data-orion-metre-step="-1"]').forEach(button => { button.disabled = !Number.isFinite(quantity) || quantity <= 1; });
      const valid = Number.isFinite(quantity) && quantity >= 1 && input.validity.valid && available;
      if (page.addButton) page.addButton.disabled = !valid;
      if (page.mobileSubmit) page.mobileSubmit.disabled = !valid;
      if (page.addLabel) page.addLabel.textContent = available ? root.dataset.addLabel : root.dataset.unavailableLabel;
      if (hint) hint.hidden = Number.isFinite(quantity) && quantity >= 1;
      root.querySelectorAll('[data-orion-price]').forEach(node => { node.textContent = page.money(basePrice * 100); });
      if (!valid) { calculated.value = ''; return; }
      const total = basePrice * quantity;
      calculated.value = total.toFixed(2);
      root.querySelectorAll('[data-orion-order-price], [data-orion-mobile-price]').forEach(node => { node.textContent = page.money(Math.round(total * 100)); });
      root.querySelectorAll('[data-orion-metre-count]').forEach(node => { node.textContent = `${String(quantity).replace('.', ',')} ${unit}`; });
    }
    function normalize() {
      if (input.value.trim() === '') input.value = '1';
      if (Number.isFinite(read())) {
        const parts = input.value.trim().replace(',', '.').split('.');
        const fraction = parts[1] || '';
        // Round the decimal digits directly, so that halves such as 1.55 always round up.
        const tenths = Number(parts[0] || '0') * 10 + Number(fraction[0] || '0') + (Number(fraction[1] || '0') >= 5 ? 1 : 0);
        if (Number.isFinite(tenths)) {
          input.value = String(Math.max(10, tenths) / 10).replace('.', ',');
          last = input.value;
        }
      }
      update();
    }
    input.addEventListener('beforeinput', event => { if (event.data && /[^\d.,]/.test(event.data)) event.preventDefault(); });
    input.addEventListener('input', () => {
      if (input.value.trim() === '') { input.value = '1'; if (document.activeElement === input) input.select(); }
      if (/^\d*(?:[.,]\d*)?$/.test(input.value)) last = input.value; else input.value = last;
      update();
    });
    input.addEventListener('change', normalize);
    input.addEventListener('blur', normalize);
    input.addEventListener('keydown', event => { if (event.key !== 'Enter') return; event.preventDefault(); normalize(); input.blur(); });
    root.querySelectorAll('[data-orion-metre-step]').forEach(button => button.addEventListener('click', () => {
      const now = Number.isFinite(read()) ? read() : 1;
      input.value = String(Math.max(1, Math.round((now + Number(button.dataset.orionMetreStep)) * 10) / 10)).replace('.', ',');
      last = input.value;
      update();
    }));
    // The form is sent by script and without validation, so the minimum is enforced before it goes.
    form?.addEventListener('submit', event => {
      normalize();
      if (!input.checkValidity() || read() < 1 || !available) {
        event.preventDefault();
        event.stopImmediatePropagation();
        input.reportValidity();
      } else {
        input.value = String(read());
        last = input.value;
      }
    }, true);
    normalize();
    return { update };
  }

  // Foams cut to size (sections/main-product-CustomFoam.liquid, kind 'foam'): the same fields and the same sum as the
  // old page, unrounded as it sent it: the price per m³ (the product's price, or its price before the discount less
  // the discount) times height × width × length in cm over 1,000,000; a foam sold by the length (tag only-length)
  // costs its price per metre times the metres. The width and length come only from the four sizes, as before.
  function foam(root, page) {
    const block = root.querySelector('[data-orion-foam]');
    if (!block) return null;
    const d = block.dataset;
    const onlyLength = d.onlyLength === 'true';
    const price = Number(d.price) || 0;
    const compareAtPrice = Number(d.compare) || 0;
    const hasDiscount = compareAtPrice > price;
    const discountPercentage = hasDiscount ? ((compareAtPrice - price) / compareAtPrice) * 100 : 0;
    const heightInput = block.querySelector('[data-orion-foam-height]');
    const widthInput = block.querySelector('[data-orion-foam-width]');
    const lengthInput = block.querySelector('[data-orion-foam-length]');
    const calculated = block.querySelector('[data-orion-foam-calculated]');
    const hint = block.querySelector('[data-orion-foam-hint]');
    const presets = [...block.querySelectorAll('[data-orion-foam-preset]')];
    let available = true;
    function update(variant) {
      if (variant) available = !!variant.a;
      let ok;
      let finalPrice = 0;
      if (onlyLength) {
        const length = parseInt(lengthInput.value);
        ok = !!length;
        if (!length) lengthInput.value = '';
        if (ok) finalPrice = hasDiscount ? (compareAtPrice / 100) * (length) : (price / 100) * (length);
      } else {
        const width = parseInt(widthInput.value);
        const length = parseInt(lengthInput.value);
        const height = parseInt(heightInput.value);
        ok = !!(width && length && height);
        // As the old page did, a field that does not read as a whole number is emptied.
        if (!height) heightInput.value = '';
        if (!width) widthInput.value = '';
        if (!length) lengthInput.value = '';
        if (ok) finalPrice = hasDiscount ? (compareAtPrice / 100) * ((height * width * length) / 1000000) : (price / 100) * ((height * width * length) / 1000000);
      }
      const value = discountPercentage ? finalPrice - finalPrice * (discountPercentage / 100) : finalPrice;
      calculated.value = value;
      const valid = ok && available;
      if (page.addButton) page.addButton.disabled = !valid;
      if (page.mobileSubmit) page.mobileSubmit.disabled = !valid;
      if (page.addLabel) page.addLabel.textContent = available ? root.dataset.addLabel : root.dataset.unavailableLabel;
      if (hint) hint.hidden = ok;
      const priceNodes = root.querySelectorAll('[data-orion-price], [data-orion-order-price], [data-orion-mobile-price]');
      if (ok) {
        priceNodes.forEach(node => { node.textContent = page.money(Math.round(value * 100)); });
        const size = d.for + ' ' + (onlyLength ? `${lengthInput.value} ${d.metre}` : `${widthInput.value} × ${lengthInput.value} × ${heightInput.value} ${d.unit}`);
        page.nameNodes.forEach(node => { node.textContent = size; });
      } else {
        priceNodes.forEach(node => { node.textContent = page.money(price); });
        page.nameNodes.forEach(node => { node.textContent = d.per; });
      }
      presets.forEach(button => {
        const active = button.dataset.width === widthInput?.value && button.dataset.length === lengthInput?.value;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }
    presets.forEach(button => button.addEventListener('click', () => {
      widthInput.value = button.dataset.width;
      lengthInput.value = button.dataset.length;
      update();
    }));
    [heightInput, widthInput, lengthInput].forEach(input => input?.addEventListener('input', () => update()));
    return { update };
  }

  const scan = scope => scope.querySelectorAll('[data-orion-product]').forEach(root => init(root));
  scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
})();
